import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Typography,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  Chip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

import { useAppTranslation } from "../../hooks/useAppTranslation";
import { usePermissions } from "../../hooks/usePermissions";
import { validateIBAN } from "../../utils/validateIbanNumber";

type CurrencyOption = {
  code: string;
  description?: string;
};

type MfSubject = {
  name: string;
  nip: string;
  statusVat: string;
  regon?: string;
  residenceAddress?: string;
  workingAddress?: string;
};

type AddVendorConfirmPayload = {
  name: string;
  nip?: string;
  residenceAddress?: string;
  workingAddress?: string;
  address?: string;
  email?: string;
  countryCode?: string;
  bankAccountNo?: string;
  iban?: string;
  currencyCode?: string;
  defaultBankAccount?: boolean;
  city?: string;
  postCode?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: AddVendorConfirmPayload) => void;
  companyId: string;
  isSubmitting?: boolean;
  submitError?: string | null;
};

export default function AddVendorModal({
  open,
  onClose,
  onConfirm,
  companyId,
  isSubmitting = false,
  submitError = null,
}: Props) {
  const { canManageBankAccounts } = usePermissions();
  const { t } = useAppTranslation("common");

  const [mode, setMode] = React.useState<"nip" | "manual">("nip");

  const [nip, setNip] = React.useState("");
  const [data, setData] = React.useState<MfSubject | null>(null);
  const [exists, setExists] = React.useState<boolean | null>(null);
  const [manualExists, setManualExists] = React.useState<boolean | null>(null);

  const [name, setName] = React.useState("");
  const [manualNip, setManualNip] = React.useState("");
  const [manualAddress, setManualAddress] = React.useState("");
  const [manualEmail, setManualEmail] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("PL");
  const [manualCity, setManualCity] = React.useState("");
  const [manualPostCode, setManualPostCode] = React.useState("");

  const [bankAccountNo, setBankAccountNo] = React.useState("");
  const [iban, setIban] = React.useState("");
  const [isDefault, setIsDefault] = React.useState(false);
  const [currencies, setCurrencies] = React.useState<CurrencyOption[]>([]);
  const [selectedCurrency, setSelectedCurrency] = React.useState("PLN");

  const [fetchingMf, setFetchingMf] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const resetForm = React.useCallback(() => {
    setMode("nip");

    setNip("");
    setData(null);
    setExists(null);
    setManualExists(null);

    setName("");
    setManualNip("");
    setManualAddress("");
    setManualEmail("");
    setCountryCode("PL");
    setManualCity("");
    setManualPostCode("");

    setBankAccountNo("");
    setIban("");
    setIsDefault(false);
    setSelectedCurrency("PLN");

    setFetchingMf(false);
    setError(null);
  }, []);

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  React.useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  React.useEffect(() => {
    setError(null);
    setExists(null);
    setManualExists(null);
  }, [mode]);

  React.useEffect(() => {
    if (!open) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/vendors/currencies?companyId=${companyId}`,
      {
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((items) => setCurrencies(items || []))
      .catch(() => setCurrencies([]));
  }, [open, companyId]);

  const hasCountryCode = /^[A-Z]{2}/.test(iban);
  const isValidIban = validateIBAN(iban) && hasCountryCode;

  const isBankDataInvalid =
    (!!iban && !isValidIban) ||
    (!iban && !!bankAccountNo) ||
    (!!iban && !bankAccountNo);

  const fetchFromMf = async () => {
    setFetchingMf(true);
    setError(null);
    setData(null);
    setExists(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/mf/nip/${nip}`,
        { credentials: "include" }
      );

      if (!res.ok) {
        throw new Error(t("Nie znaleziono podmiotu"));
      }

      const json = await res.json();
      const subject = json?.result?.subject;

      if (!subject) {
        throw new Error(t("Brak danych podmiotu"));
      }

      setData(subject);

      const resExists = await fetch(
        `${process.env.REACT_APP_API_URL}/vendors/exists?companyId=${companyId}&nip=${subject.nip}`,
        { credentials: "include" }
      );

      const jsonExists = await resExists.json();
      setExists(jsonExists.exists);
    } catch (e: any) {
      setError(e.message || t("Błąd pobierania danych"));
    } finally {
      setFetchingMf(false);
    }
  };

  const checkManualDuplicate = async () => {
    if (!manualNip || manualNip.trim().length < 3) {
      setManualExists(null);
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/vendors/exists?companyId=${companyId}&nip=${manualNip.trim()}`,
        { credentials: "include" }
      );
      const json = await res.json();
      setManualExists(json.exists);
    } catch {
      setManualExists(null);
    }
  };

  if (!open) return null;

  const showBankSection =
    canManageBankAccounts &&
    ((mode === "nip" && !!data && exists !== true) || mode === "manual");

  const showBankError = iban.length > 0 || bankAccountNo.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          py: 2,
        }}
      >
        <PersonAddAltIcon />
        <Box>
          <Typography variant="h6" fontWeight={600} color="inherit">
            {t("Dodaj dostawcę")}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {mode === "nip"
              ? t("Pobierz dane z rejestru MF")
              : t("Wprowadź dane ręcznie")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && !isSubmitting && setMode(v)}
          size="small"
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton value="nip" disabled={isSubmitting}>
            <SearchIcon sx={{ mr: 0.5, fontSize: 18 }} />
            {t("Z GUS/MF")}
          </ToggleButton>
          <ToggleButton value="manual" disabled={isSubmitting}>
            <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
            {t("Ręcznie")}
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "nip" && (
          <>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label={t("NIP")}
                value={nip}
                onChange={(e) => {
                  setNip(e.target.value.replace(/\D/g, ""));
                  setData(null);
                  setExists(null);
                  setError(null);
                }}
                fullWidth
                size="small"
                disabled={isSubmitting}
                helperText={t("Wpisz 10-cyfrowy NIP")}
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">PL</InputAdornment>
                  ),
                }}
              />

              <Button
                onClick={fetchFromMf}
                disabled={nip.length !== 10 || fetchingMf || isSubmitting}
                variant="contained"
                sx={{ minWidth: 130, height: 40, mt: "1px" }}
                startIcon={
                  fetchingMf ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
              >
                {fetchingMf ? t("Pobieranie...") : t("Pobierz")}
              </Button>
            </Stack>

            <Collapse in={!!data} timeout={300}>
              {data && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#F8FAFC",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircleIcon color="success" sx={{ mt: 0.3 }} />
                    <Box flex={1}>
                      <Typography fontWeight={600} variant="body1">
                        {data.name}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mt={0.5}
                      >
                        <Typography variant="body2" color="text.secondary">
                          NIP: {data.nip}
                        </Typography>

                        {data.regon && (
                          <Typography variant="body2" color="text.secondary">
                            · REGON: {data.regon}
                          </Typography>
                        )}

                        <Chip
                          label={data.statusVat}
                          size="small"
                          color={
                            data.statusVat === "Czynny" ? "success" : "default"
                          }
                          variant="outlined"
                          sx={{ ml: "auto" }}
                        />
                      </Stack>

                      {(data.residenceAddress || data.workingAddress) && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          {data.workingAddress || data.residenceAddress}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
            </Collapse>

            {exists === true && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t("Dostawca z tym NIP-em już istnieje w bazie")}
              </Alert>
            )}
          </>
        )}

        {mode === "manual" && (
          <Stack spacing={2}>
            <TextField
              label={t("Nazwa firmy")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
              disabled={isSubmitting}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label={t("Kod kraju")}
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                size="small"
                sx={{ width: 120 }}
                inputProps={{ maxLength: 2 }}
                disabled={isSubmitting}
              />

              <TextField
                label={t("VAT / NIP")}
                value={manualNip}
                onChange={(e) => {
                  setManualNip(e.target.value);
                  setManualExists(null);
                }}
                onBlur={checkManualDuplicate}
                fullWidth
                size="small"
                disabled={isSubmitting}
              />
            </Stack>

            <TextField
              label={t("Adres")}
              value={manualAddress}
              onChange={(e) => setManualAddress(e.target.value)}
              fullWidth
              size="small"
              disabled={isSubmitting}
            />

            <TextField
              label={t("Email")}
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              disabled={isSubmitting}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label={t("Miasto")}
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                fullWidth
                size="small"
                disabled={isSubmitting}
              />

              <TextField
                label={t("Kod pocztowy")}
                value={manualPostCode}
                onChange={(e) => setManualPostCode(e.target.value)}
                size="small"
                sx={{ width: 160 }}
                disabled={isSubmitting}
              />
            </Stack>

            {manualExists === true && (
              <Alert severity="warning">
                {t("Dostawca z tym NIP/VAT już istnieje w bazie")}
              </Alert>
            )}
          </Stack>
        )}

        <Collapse in={showBankSection} timeout={300}>
          <Divider sx={{ my: 2.5 }} />

          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <AccountBalanceIcon
              sx={{ fontSize: 20, color: "text.secondary" }}
            />
            <Typography variant="subtitle2" color="text.secondary">
              {t("Konto bankowe")}
            </Typography>
            <Chip label={t("opcjonalne")} size="small" variant="outlined" />
          </Stack>

          <Stack spacing={2}>
            <TextField
              label={t("Numer konta")}
              value={bankAccountNo}
              onChange={(e) => setBankAccountNo(e.target.value)}
              size="small"
              fullWidth
              disabled={isSubmitting}
            />

            <TextField
              label="IBAN"
              value={iban}
              onChange={(e) => setIban(e.target.value.toUpperCase())}
              size="small"
              fullWidth
              disabled={isSubmitting}
              error={iban.length > 0 && !isValidIban}
              helperText={
                iban.length > 0 && !isValidIban
                  ? t("Nieprawidłowy IBAN (błąd checksum lub kod kraju)")
                  : ""
              }
            />

            <Stack direction="row" spacing={2}>
              <Autocomplete
                options={currencies}
                getOptionLabel={(o) => o?.code || ""}
                value={
                  currencies.find((c) => c.code === selectedCurrency) || null
                }
                onChange={(_, v) => setSelectedCurrency(v?.code || "PLN")}
                sx={{ flex: 1 }}
                disabled={isSubmitting}
                renderInput={(params) => (
                  <TextField {...params} label={t("Waluta")} size="small" />
                )}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    disabled={isSubmitting}
                  />
                }
                label={t("Domyślne")}
              />
            </Stack>
          </Stack>

          {showBankError && isBankDataInvalid && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {t("IBAN i numer konta muszą być uzupełnione poprawnie")}
            </Alert>
          )}
        </Collapse>

        {isSubmitting && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("Trwa dodawanie dostawcy...")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          {t("Anuluj")}
        </Button>

        <Button
          variant="contained"
          disabled={
            isSubmitting ||
            fetchingMf ||
            isBankDataInvalid ||
            (mode === "nip" && (!data || exists === true)) ||
            (mode === "manual" && (!name.trim() || manualExists === true))
          }
          onClick={() => {
            if (mode === "nip" && data) {
              onConfirm({
                ...data,
                bankAccountNo: bankAccountNo || undefined,
                iban: iban || undefined,
                currencyCode: selectedCurrency,
                defaultBankAccount: isDefault,
              });
              return;
            }

            if (mode === "manual") {
              onConfirm({
                name: name.trim(),
                nip: manualNip.trim() || undefined,
                address: manualAddress.trim() || undefined,
                email: manualEmail.trim() || undefined,
                countryCode: countryCode.trim().toUpperCase() || "PL",
                city: manualCity.trim() || undefined,
                postCode: manualPostCode.trim() || undefined,
                bankAccountNo: bankAccountNo.trim() || undefined,
                iban: iban.trim() || undefined,
                currencyCode: selectedCurrency,
                defaultBankAccount: isDefault,
              });
            }
          }}
        >
          {isSubmitting ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>{t("Dodawanie...")}</span>
            </Stack>
          ) : (
            t("Dodaj dostawcę")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}